
import React, { useState, useEffect } from 'react';
import { UserGroupIcon } from '@heroicons/react/24/outline';
import { StatusBadge } from '../../../shared/components/badges/StatusBadge';
import AppointmentExpandedRow from '../components/AppointmentExpandRow';
import TabbedForm from '../../../shared/components/forms/FormTabs';
import { ProfileUpload } from '../../users/components/profileUpload';
import { AppointmentService } from '../../../shared/services/appointmentService';
import { VaccineService } from '../../../shared/services/vaccineService';
import { PatientService } from '../../../shared/services/patientService';
import { MedicalStaffService } from '../../../shared/services/medicalStaffService';
import { CenterService } from '../../../shared/services/centerService';
import { StockService } from '../../../shared/services/stockService';

const AppointmentStatusBadge = ({ status }) => {
  const statusStyles = {
    SCHEDULED: "bg-blue-100 text-blue-800",
    scheduled: "bg-blue-100 text-blue-800",
    CONFIRMED: "bg-green-100 text-green-800",
    confirmed: "bg-green-100 text-green-800",
    COMPLETED: "bg-purple-100 text-purple-800",
    completed: "bg-purple-100 text-purple-800",
    CANCELLED: "bg-red-100 text-red-800",
    cancelled: "bg-red-100 text-red-800",
    RESCHEDULED: "bg-orange-100 text-orange-800",
    rescheduled: "bg-orange-100 text-orange-800",
    START_VISIT: "bg-teal-100 text-teal-800",
    CHECK_IN: "bg-cyan-100 text-cyan-800",
    CHECK_OUT: "bg-indigo-100 text-indigo-800",
    UNVISITED: "bg-yellow-100 text-yellow-800",
    "no-show": "bg-yellow-100 text-yellow-800",
  };

  const statusLabels = {
    SCHEDULED: "Scheduled",
    scheduled: "Scheduled",
    CONFIRMED: "Confirmed",
    confirmed: "Confirmed",
    COMPLETED: "Completed",
    completed: "Completed",
    CANCELLED: "Cancelled",
    cancelled: "Cancelled",
    RESCHEDULED: "Rescheduled",
    rescheduled: "Rescheduled",
    START_VISIT: "Start Visit",
    CHECK_IN: "Check In",
    CHECK_OUT: "Check Out",
    UNVISITED: "Unvisited",
    "no-show": "No Show",
  };

  return <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusStyles[status] || "bg-gray-100 text-gray-800"}`}>{statusLabels[status] || status}</span>;
};

// Convert 24-hour time to 12-hour AM/PM format
const formatTime12Hour = (time) => {
  if (!time) return '—';
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};
// Load parents
export const getParentOptions = async () => {
  try {
    const res = await AppointmentService.getParents({ status: "ACTIVE", limit: 5 });
    const data = res?.data?.data?.parents || res?.data?.parents || res?.data || [];
    
    return data.map(parent => ({
      value: parent.id || parent._id,
      label: parent.fullName || parent.parentName || parent.name
    }));
  } catch (error) {
    console.error("Failed to load parents", error);
    return [];
  }
};

// Load children by parent ID (filter out deleted children)
export const getChildOptions = async (formData) => {
  const parentId = formData?.parentId;
  if (!parentId) return [];
  
  try {
    const res = await AppointmentService.getChildrenByParent(parentId);
    const data = res?.data?.data?.children || res?.data?.children || res?.data || [];
    
    // Filter: only show children where isActive is true
    const activeChildren = data.filter(child => child.isActive === true);
    
    return activeChildren.map(child => ({
      value: child.id || child._id || child.childId,
      label: child.name || child.childName || child.firstName || `Child ${child.id}`
    }));
  } catch (error) {
    console.error("Failed to load children", error);
    return [];
  }
};

// Generic loader for dropdowns from API with fallback
const loadDropdownOptions = async (key, fallback = []) => {
  try {
    const res = await AppointmentService.getDropdowns();
    const raw =
      res?.data?.data?.[key] ||
      res?.data?.[key] ||
      res?.[key] ||
      [];

    return raw.map(item => ({
      value: item.value,
      label: item.label
    }));
  } catch (error) {
    console.warn(`Failed to load ${key} from API. Using fallback`, error);
    return fallback;
  }
};

export const getCenterOptions = async () => {
  try {
    const res = await CenterService.getAll({ status: "ACTIVE", limit: 5 });

    const centers =
      res?.data?.data?.vaccinationCenters ||
      res?.data?.vaccinationCenters ||
      res?.data?.data?.centers ||     // fallback
      res?.data?.centers ||           // fallback
      [];

    return centers.map(center => ({
      value: center.id || center._id,
      label: center.name || center.centerName
    }));
  } catch (error) {
    console.error("Failed to load centers", error);
    return [];
  }
};


// Load medical staff
export const getMedicalStaffOptions = async () => {
  try {
    const res = await MedicalStaffService.getAll({ status: "ACTIVE", limit: 5 });
    const data = res?.data?.data?.medicalStaff || res?.data?.medicalStaff || res?.data || [];
    
    return data.map(staff => ({
      value: staff.id || staff._id,
      label: staff.fullName || staff.name || `${staff.firstName || ''} ${staff.lastName || ''}`.trim()
    }));
  } catch (error) {
    console.error("Failed to load medical staff", error);
    return [];
  }
};

// Options loaders
export const getVaccineOptions = async () => {
  try {
    const res = await VaccineService.getAll({ status: 'ACTIVE', limit: 5 });
    const vaccines = res?.data?.vaccines || res?.data?.data?.vaccines || [];
    return vaccines.map(v => ({
      value: v.id,
      label: v.name
    }));
  } catch (error) {
    console.error('Failed to load vaccines', error);
    return [];
  }
};
// export const getDoctorOptions = () => loadDropdownOptions('doctors');
export const getStatusOptions = () => loadDropdownOptions('appointmentStatuses');
export const getPriorityOptions = () => loadDropdownOptions('priorities');
export const getRelationOptions = () => loadDropdownOptions('relations');
export const getGenderOptions = () => loadDropdownOptions('genders');
export const getBloodGroupOptions = () => loadDropdownOptions('bloodGroups');

const AppointmentFormLogic = ({ formFields, formData, handleFormChange, renderFormField, activeTab, setActiveTab }) => {

  const [slotsData, setSlotsData] = useState(null);

  useEffect(() => {
    const fetchSlots = async () => {
      if (!formData.clinicId) {
        setSlotsData(null);
        return;
      }
      try {
        const data = await AppointmentService.getCenterSlots(formData.clinicId);
        setSlotsData(data);
      } catch (error) {
        console.error("Failed to load slots", error);
      }
    };
    fetchSlots();
  }, [formData.clinicId]);

  const tabsConfig = {
     parent: { label: 'Parent Information' },
     appointment: { label: 'Appointment Details' },
  };

  const wrappedRenderFormField = (field) => {
    // Inject min/max for date
    if (field.name === 'scheduledDate') {
       const dateProps = { ...field };
       if (slotsData?.data?.dateRange) {
           dateProps.min = slotsData.data.dateRange.from;
           dateProps.max = slotsData.data.dateRange.to;
       }
       return renderFormField(dateProps);
    }

    // Inject options for time
    if (field.name === 'scheduledTime') {
        const selectedDate = formData.scheduledDate;
        
        // Disable if no date selected or no slots data
        if (!slotsData || !selectedDate) {
             return renderFormField({ 
                 ...field, 
                 disabled: true, 
                 placeholder: !formData.clinicId ? 'Select Center first' : 'Select Date first' 
             });
        }

        // Find slots for date (robust comparison)
        const dateSlots = slotsData.data?.dates?.find(d => {
             // Ensure string comparison
             return String(d.date).trim() === String(selectedDate).trim();
        });
        
        // Show ALL slots, but mark unavailable ones as disabled
        const allSlots = dateSlots?.slots || [];
        
        const timeOptions = allSlots.map(s => ({
            value: s.time,
            label: formatTime12Hour(s.time),
            disabled: !s.isAvailable
        }));

        return renderFormField({
            ...field,
            type: 'select', // Switch to dropdown
            options: timeOptions,
            placeholder: timeOptions.length > 0 ? 'Select time' : 'No slots available for this date',
            disabled: timeOptions.length === 0
        });
    }

    return renderFormField(field);
  };

  return (
    <TabbedForm 
        formFields={formFields} 
        formData={formData} 
        handleFormChange={handleFormChange} 
        renderFormField={wrappedRenderFormField} 
        tabsConfig={tabsConfig} 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
    />
  );
};

export const appointmentConfig = {
  title: 'Appointment',
  endpoint: 'admin/appointments',
  crudEndpoint: 'appointments',
  storageKey: 'appointments',
  useUrlNavigation: true,
  basePath: '/admin/Appointments',
  statusOptionsLoader: getStatusOptions,

  // Enable statistics cards
  showStats: true,
  statsConfig: {
    statsOrder: ['total', 'completed', 'pending', 'cancelled', 'scheduled'],
    statsLabels: {
      total: 'Total Appointments',
      completed: 'Completed',
      pending: 'Pending',
      cancelled: 'Cancelled',
      scheduled: 'Scheduled'
    }
  },

  columns: [
    {
      key: 'parentName',
      label: 'Parent Name',
      render: (item) => item.parent?.fullName || item.parent?.parentName || item.parentName || '—'
    },
    {
      key: 'childName',
      label: 'Child Name',
      render: (item) => item.child?.name || item.childName || '—'
    },
    {
      key: 'medicalStaffName',
      label: 'Medical Staff',
      render: (item) => item.medicalStaff?.fullName || item.medicalStaff?.name || item.medicalStaffName || '—'
    },
    {
      key: 'centerName',
      label: 'Vaccination Center',
      render: (item) => item.vaccinationCenter?.name || item.centerName || item.center || '—'
    },
    {
      key: 'vaccineName',
      label: 'Vaccine',
      render: (item) => item.vaccine?.name || item.vaccineName || '—'
    },
    {
      key: 'scheduledDate',
      label: 'Appointment Date',
      render: (item) => item.scheduledDate ? new Date(item.scheduledDate).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }) : '—'
    },
    {
      key: 'scheduledTime',
      label: 'Time',
      render: (item) => formatTime12Hour(item.scheduledTime)
    },
    {
      key: 'status',
      label: 'Status',
      render: (item) => <AppointmentStatusBadge status={item.status || 'scheduled'} />
    }
  ],

 formFields: [
  // Parent Information

{
  name: 'parentId',
  label: 'Parent Name',
  type: 'infinite-select',
  required: true,
  placeholder: 'Select parent',
  section: 'parent',
  fetchFn: (params) => AppointmentService.getParents(params),
  getOptions: (res) => (res?.data || []).map((p) => ({ value: p.id, label: p.fullName?.trim() || p.email })),
  getTotal: (res) => res?.pagination?.total ?? 0,
  extraParams: { status: 'ACTIVE' },
  renderValue: (value, formData) => formData.parentName || value
},
{
  name: 'childId',
  label: 'Child Name',
  type: 'select', 
  placeholder: 'Select child',
  section: 'parent',
  optionsLoader: getChildOptions,
  disabled: (formData) => !formData?.parentId,
  renderValue: (value, formData) => formData.childName || value
},
{
  name: 'medicalStaffId',
  label: 'Medical Staff',
  type: 'infinite-select',
  required: false,
  requiredWhen: (formData) => formData.status === 'COMPLETED',
  requiredWhenMessage: 'Medical Staff is required to complete the appointment',
  placeholder: 'Select medical staff',
  section: 'parent',
  fetchFn: (params) => MedicalStaffService.getAll(params),
  getOptions: (res) => (res?.data?.data?.medicalStaff || res?.data?.medicalStaff || res?.data || []).map((s) => ({ value: s.id || s._id, label: s.fullName || s.name || `${s.firstName || ''} ${s.lastName || ''}`.trim() })),
  getTotal: (res) => res?.pagination?.total ?? 0,
  extraParams: { status: 'ACTIVE' },
  renderValue: (value, formData) => formData.medicalStaffName || value
},

  // {
  //   name: 'parentName',
  //   label: 'Parent Name',
  //   type: 'text',
  //   placeholder: 'Enter parent name',
  //   section: 'parent',
  //   // required: true
  // },
  // {
  //   name: 'parentEmail',
  //   label: 'Parent Email',
  //   type: 'email',
  //   placeholder: 'parent@email.com',
  //   section: 'parent',
  //   // required: true
  // },
  // {
  //   name: 'parentPhone',
  //   label: 'Parent Phone',
  //   type: 'tel',
  //   placeholder: '+91-XXXXXXXXXX',
  //   section: 'parent',
  //   // required: true
  // },
  // {
  //   name: 'relationWithChild',
  //   label: 'Relation with Child',
  //   type: 'select',
  //   optionsLoader: getRelationOptions,
  //   placeholder: 'Select relation',
  //   section: 'parent',
  //   // required: true
  // },
  // {
  //   name: 'address',
  //   label: 'Address',
  //   type: 'textarea',
  //   rows: 3,
  //   placeholder: 'Enter complete address...',
  //   fullWidth: true,
  //   section: 'parent',
  //   // optional
  // },
  
  // Child Information - Profile Picture MUST come first in the child section
  // {
  //   name: "profilePicture",
  //   label: "Child Profile Picture",
  //   accept: 'image/*',
  //   placeholder: 'Upload child profile picture (optional)',
  //   section: 'child',
  //   fullWidth: true,
  //   customRender: (field, value, onChange, error) => (
  //     <ProfileUpload field={field} value={value} onChange={onChange} error={error} />
  //   ),
  //   // optional
  // },
  // {
  //   name: 'childName',
  //   label: 'Child Name',
  //   type: 'text',
  //   placeholder: 'Enter child\'s full name',
  //   section: 'child',
  //   // required: true
  // },
  // {
  //   name: 'childGender',
  //   label: 'Gender',
  //   type: 'select',
  //   optionsLoader: getGenderOptions,
  //   placeholder: 'Select gender',
  //   section: 'child',
  //   // required: true
  // },
  // {
  //   name: 'dateOfBirth',
  //   label: 'Date of Birth',
  //   type: 'date',
  //   section: 'child',
  //   // required: true
  // },
  // {
  //   name: 'childAge',
  //   label: 'Age',
  //   type: 'text',
  //   placeholder: 'e.g., 2 years or 6 months',
  //   section: 'child',
  //   // optional
  // },
  // {
  //   name: 'weight',
  //   label: 'Weight (kg)',
  //   type: 'number',
  //   placeholder: 'Enter weight in kg',
  //   section: 'child',
  //   // optional
  // },
  // {
  //   name: 'bloodGroup',
  //   label: 'Blood Group',
  //   type: 'select',
  //   optionsLoader: getBloodGroupOptions,
  //   placeholder: 'Select blood group',
  //   section: 'child',
  //   // optional
  // },
  
  // Appointment Details
  {
    name: 'vaccineId',
    label: 'Vaccine Name',
    type: 'infinite-select',
    placeholder: 'Select a vaccine',
    section: 'appointment',
    fetchFn: (params) => VaccineService.getAll(params),
    getOptions: (res) => (res?.data?.vaccines || res?.data || []).map((v) => ({ value: v.id, label: v.name })),
    getTotal: (res) => res?.data?.pagination?.total ?? 0,
    extraParams: { status: 'ACTIVE' },
    required: true,
    renderValue: (value, formData) => formData.vaccineName || value
  },
  {
    name: 'clinicId',
    label: 'Vaccination Center',
    type: 'infinite-select',
    required: true,
    placeholder: 'Select center',
    section: 'appointment',
    fetchFn: (params) => CenterService.getAll(params),
    getOptions: (res) => (res?.data?.data?.vaccinationCenters || res?.data?.vaccinationCenters || res?.data?.data?.centers || res?.data?.centers || []).map((c) => ({ value: c.id || c._id, label: c.name || c.centerName })),
    getTotal: (res) => res?.data?.pagination?.total ?? 0,
    extraParams: { status: 'ACTIVE' },
    renderValue: (value, formData) => formData.centerName || formData.center || value
  },
  {
    name: 'scheduledDate',
    label: 'Appointment Date',
    type: 'date',
    section: 'appointment',
    requiredWhen: (formData) => formData.status === 'COMPLETED',
    requiredWhenMessage: 'Appointment Date is required to complete the appointment',
  },
  {
    name: 'scheduledTime',
    label: 'Appointment Time',
    type: 'time',
    section: 'appointment',
    requiredWhen: (formData) => formData.status === 'COMPLETED',
    requiredWhenMessage: 'Appointment Time is required to complete the appointment',
    disabled: (formData) => formData.center && formData.doctor
  },
  // {
  //   name: 'doctor',
  //   label: 'Select Doctor',
  //   type: 'select',
  //   optionsLoader: getDoctorOptions,
  //   placeholder: 'Select a doctor',
  //   section: 'appointment',
  //   disabled: (formData) => !formData.center,
  // },
//   {
//   name: 'doctor',
//   label: 'Select Doctor',
//   type: 'select',
//   optionsLoader: getDoctorOptions, // ✅ use new loader
//   placeholder: 'Select a doctor',
//   section: 'appointment',
//   disabled: (formData) => !formData.center,
// },

  // {
  //   name: 'status',
  //   label: 'Status',
  //   type: 'select',
  //   optionsLoader: getStatusOptions,
  //   default: 'scheduled',
  //   section: 'appointment',
  //   // optional
  // },
  // {
  //   name: 'priority',
  //   label: 'Priority',
  //   type: 'select',
  //   optionsLoader: getPriorityOptions,
  //   default: 'medium',
  //   section: 'appointment',
  //   // optional
  // },
  {
    name: 'status',
    label: 'Status',
    type: 'select',
    required: true,
    placeholder: 'Select status',
    section: 'appointment',
    options: [
      { value: 'SCHEDULED', label: 'Scheduled' },
      { value: 'CONFIRMED', label: 'Confirmed' },
      { value: 'COMPLETED', label: 'Completed' },
      { value: 'CANCELLED', label: 'Cancelled' },
      { value: 'RESCHEDULED', label: 'Rescheduled' },
      { value: 'START_VISIT', label: 'Start Visit' },
      { value: 'CHECK_IN', label: 'Check In' },
      { value: 'CHECK_OUT', label: 'Check Out' },
      { value: 'UNVISITED', label: 'Unvisited' },
    ],
  },
  {
    name: 'cancellationReason',
    label: 'Cancellation Reason',
    type: 'textarea',
    rows: 3,
    placeholder: 'Enter reason for cancellation...',
    fullWidth: true,
    section: 'appointment',
    hidden: (formData) => formData?.status !== 'CANCELLED' && formData?.status !== 'cancelled',
  },
  {
    name: 'vaccineInventoryId',
    label: 'Vaccine Inventory',
    type: 'infinite-select',
    placeholder: 'Select vaccine inventory',
    requiredWhen: (formData) => formData.status === 'COMPLETED',
    requiredWhenMessage: 'Vaccine Inventory is required to complete the appointment',
    section: 'appointment',
    fetchFn: (params) => StockService.getAll(params),
    getOptions: (res) => {
      const items = res?.data?.inventory;
      if (!Array.isArray(items)) return [];
      return items.map((inv) => ({
        value: inv.id,
        label: `${inv.vaccine?.name || inv.vaccineName || inv.id} — Batch: ${inv.batchNumber || 'N/A'} (Qty: ${inv.quantity ?? '?'})`
      }));
    },
    getTotal: (res) => res?.data?.pagination?.total ?? 0,
    extraParams: { status: 'ACTIVE' },
    renderValue: (value, formData) => formData.vaccineInventoryName || value,
  },
  {
    name: 'isParentAcknowledged',
    label: 'Parent Acknowledged',
    type: 'checkbox',
    section: 'appointment',
  },
  {
    name: 'isActive',
    label: 'Active',
    type: 'checkbox',
    section: 'appointment',
  },
  {
    name: 'notes',
    label: 'Notes',
    type: 'textarea',
    rows: 4,
    placeholder: 'Additional notes or special instructions...',
    fullWidth: true,
    section: 'appointment',
  }
],


  searchFields: ['parentName', 'childName', 'vaccineName', 'doctor', 'center', 'parentEmail', 'parentPhone'],

  customFilters: [
    {
      key: 'clinicId',
      type: 'infinite-multiselect',
      label: 'Vaccination Center',
      placeholder: 'All Centers',
      fetchFn: (params) => CenterService.getAll(params),
      getOptions: (res) =>
        (res?.data?.vaccinationCenters || res?.data?.data?.vaccinationCenters || res?.data?.centers || []).map((c) => ({
          value: c.id || c._id,
          label: c.name || c.centerName,
        })),
      getTotal: (res) => res?.data?.pagination?.total ?? 0,
    },
  ],

   formState: { activeTab: 'parent' },

  customHandlers: {
    onView: async (item) => {
      const response = await AppointmentService.getById(item.id);
      const data = response?.data || response;
      
      // Fetch parent details
      if (data.parentId) {
        try {
          const parentRes = await PatientService.getById(data.parentId);
          const parent = parentRes?.data || parentRes;
          console.log('Fetched parent details:', parent);
          if (parent) {
            data.parentName = parent.fullName || parent.parentName || parent.name;
          }
        } catch (error) {
          console.error('Failed to fetch parent name', error);
        }
      }
      
      if (data.child) {
        data.childId = data.child.id;
        data.childName = data.child.name;
      }
      
      if (data.vaccine) {
        data.vaccineId = data.vaccine.id;
        data.vaccineName = data.vaccine.name;
      }
      
      if (data.vaccinationCenter) {
        data.clinicId = data.vaccinationCenter.id;
        data.centerName = data.vaccinationCenter.name;
        data.center = data.vaccinationCenter.name;
      }
      
      if (data.medicalStaff) {
        data.medicalStaffId = data.medicalStaff.id;
        data.medicalStaffName = data.medicalStaff.fullName || data.medicalStaff.name;
      } else if (data.medicalStaffId && typeof data.medicalStaffId === 'string') {
        try {
          const staffRes = await MedicalStaffService.getById(data.medicalStaffId);
          const staff = staffRes?.data || staffRes;
          data.medicalStaffName = staff.fullName || staff.name;
        } catch (error) {
          console.error('Failed to fetch medical staff name', error);
        }
      }

      // Map boolean/nullable fields explicitly so form shows correct values
      data.isParentAcknowledged = data.isParentAcknowledged ?? false;
      data.isActive = data.isActive ?? true;
      data.cancellationReason = data.cancellationReason || '';
      data.vaccineInventoryId = data.vaccineInventoryId || '';

      return data;
    },
    transformData: (response) => {
      const appointments = response?.data?.appointments || 
                          response?.data?.data?.appointments || 
                          response?.data?.data || 
                          response?.data || 
                          response || 
                          [];
      const appointmentArray = Array.isArray(appointments) ? appointments : [];
      // Filter out cancelled appointments
      return appointmentArray.filter(apt => apt.status !== 'cancelled' && apt.status !== 'CANCELLED');
    },
    renderFormSections: (formFields, formData, handleFormChange, mode, renderFormField, activeTab, setActiveTab) => {
      return (
        <AppointmentFormLogic
          formFields={formFields}
          formData={formData}
          handleFormChange={handleFormChange}
          renderFormField={renderFormField}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      );
    },

    renderExpandedRow: (item) => <AppointmentExpandedRow item={item} />,

    processFormData: (formData, mode) => {
      const payload = {
        parentId: formData.parentId,
        childId: formData.childId,
        vaccineId: formData.vaccineId,
        scheduledDate: formData.scheduledDate,
        scheduledTime: formData.scheduledTime,
        clinicId: formData.clinicId,
        notes: formData.notes || '',
        status: formData.status,
        isParentAcknowledged: formData.isParentAcknowledged ?? false,
        isActive: formData.isActive ?? true,
      };
      if (formData.medicalStaffId) {
        payload.medicalStaffId = formData.medicalStaffId;
      }
      if (formData.cancellationReason) {
        payload.cancellationReason = formData.cancellationReason;
      }
      if (formData.vaccineInventoryId) {
        payload.vaccineInventoryId = formData.vaccineInventoryId;
      }
      return payload;
    },
  }
};
